import { useLocation, Link } from "react-router-dom";
import { CalendarCheck, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import BloqLogo from "@/components/BloqLogo";
import { formatInTz } from "@/lib/time";

export default function BookingConfirmation() {
  const { state } = useLocation();
  const booking = state?.booking;
  const link = state?.link;
  const tz = state?.tz || "UTC";
  const slot = state?.slot;

  const downloadIcs = () => {
    if (!booking) return;
    const start = booking.selected_time.replace(/[-:]/g, "").split(".")[0] + "Z";
    const dur = link?.duration_minutes || 30;
    const endDate = new Date(new Date(booking.selected_time).getTime() + dur * 60000);
    const end = endDate.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    const url = `${window.location.origin}/m/${booking.meeting_public_id}`;
    const ics = `BEGIN:VCALENDAR\nVERSION:2.0\nPRODID:-//Bloq//Meeting//EN\nBEGIN:VEVENT\nUID:${booking.meeting_public_id}@bloq.video\nDTSTAMP:${start}\nDTSTART:${start}\nDTEND:${end}\nSUMMARY:${booking.title}\nDESCRIPTION:Join: ${url}\nLOCATION:${url}\nURL:${url}\nEND:VEVENT\nEND:VCALENDAR`;
    const blob = new Blob([ics], { type: "text/calendar" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${booking.title.replace(/[^a-z0-9]/gi, "_")}.ics`;
    a.click();
  };

  if (!booking) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#fbfcfc]">
        <BloqLogo size={26} />
        <p className="text-[#4a4a52] mt-6">No booking found. Please use a booking link to schedule.</p>
        <Link to="/" className="bloq-btn-primary mt-4">Back to Bloq</Link>
      </div>
    );
  }
  const url = `${window.location.origin}/m/${booking.meeting_public_id}`;
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#fbfcfc] px-4">
      <BloqLogo size={26} />
      <div className="bloq-glass-strong p-10 max-w-md mt-6 text-center" data-testid="booking-confirmation">
        <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-4" style={{ background: "rgba(0,94,122,0.1)" }}>
          <CalendarCheck className="w-7 h-7 text-[#005e7a]" />
        </div>
        <p className="bloq-eyebrow">Booked</p>
        <h1 className="bloq-headline text-3xl mt-2 mb-3">You're on the calendar.</h1>
        <p className="text-sm text-[#4a4a52]">
          {booking.title} with {booking.host_name} on <strong>{formatInTz(slot, tz)}</strong>.
        </p>
        <p className="text-xs text-[#82828c] mt-3">Email delivery is not yet connected — save the calendar file or this link to remember the meeting.</p>
        <div className="bloq-card p-3 mt-5 text-left">
          <p className="text-[10px] uppercase tracking-widest text-[#82828c] font-medium mb-1">Meeting link</p>
          <code className="text-xs break-all">{url}</code>
        </div>
        <div className="flex gap-2 mt-5 justify-center flex-wrap">
          <button onClick={() => { navigator.clipboard.writeText(url); toast.success("Copied"); }} className="bloq-btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1.5" data-testid="copy-meeting-link"><Copy className="w-3 h-3" /> Copy link</button>
          <button onClick={downloadIcs} className="bloq-btn-secondary text-xs px-3 py-1.5" data-testid="download-ics">Add to calendar (.ics)</button>
          <Link to={url} className="bloq-btn-primary text-xs px-3 py-1.5 inline-flex items-center gap-1.5" data-testid="goto-meeting"><ExternalLink className="w-3 h-3" /> Open meeting</Link>
        </div>
      </div>
    </div>
  );
}
